import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import PasswordRequirements, { isPasswordStrong } from "@/components/PasswordRequirements";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /set-password?token=...
 * Reached from the "add a password" email. Pre-fills the email (read-only)
 * and asks for a single new password. On submit, the backend creates/links
 * the user, drops auth cookies, and we navigate to /app.
 */
export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSelf } = useAuth();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);   // { email, first_name }
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoadErr("This link is missing or invalid.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/set-password/info`, { params: { token } });
        if (!cancelled) setInfo(r.data);
      } catch (e) {
        if (!cancelled) setLoadErr(formatApiErrorDetail(e?.response?.data?.detail) || "This link is invalid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(password)) { setError("Please make your password stronger — see the requirements below."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/set-password`, { token, new_password: password }, { withCredentials: true });
      // Manually stash tokens (the AuthContext only does this from its own login/signup paths).
      try {
        if (r.data.access_token) localStorage.setItem("wv:access", r.data.access_token);
        if (r.data.refresh_token) localStorage.setItem("wv:refresh", r.data.refresh_token);
      } catch {}
      await refreshSelf();
      navigate("/app", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't set your password.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthShell title="Loading…" subtitle="One moment">
        <div className="flex items-center gap-3 text-neutral-500"><Loader2 className="w-5 h-5 animate-spin" /> Checking your link…</div>
      </AuthShell>
    );
  }
  if (loadErr) {
    return (
      <AuthShell title="That link won't work." subtitle="Set your password">
        <p className="text-[15px] text-neutral-700 mb-5">{loadErr}</p>
        <Link to="/forgot-password" className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-black bg-white hover:bg-[#FFD600] text-sm tracking-[0.18em] uppercase font-bold">
          Get a fresh link
        </Link>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Add a password." subtitle="Set up your account">
      <div className="mb-5 p-3 bg-[#FFFBE6] border border-[#FFD600] flex items-start gap-2">
        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="text-sm">
          You&rsquo;re setting a password for <strong>{info.email}</strong>. Your existing locations and topics are already linked.
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="setpw-form">
        <div>
          <label className="ticker-label !text-[10px] block mb-1.5">New password</label>
          <input
            type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="setpw-password"
            autoComplete="new-password"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
          <PasswordRequirements value={password} />
        </div>
        <div>
          <label className="ticker-label !text-[10px] block mb-1.5">Confirm password</label>
          <input
            type="password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="setpw-confirm"
            autoComplete="new-password"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
        </div>
        {error && <div data-testid="setpw-error" className="text-sm text-red-600">{error}</div>}
        <button
          type="submit" disabled={busy}
          data-testid="setpw-submit"
          className="w-full inline-flex items-center justify-center gap-2 mt-2 px-5 py-3 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-sm tracking-[0.18em] uppercase font-bold disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Save and sign in
        </button>
      </form>
    </AuthShell>
  );
}
